import type { NodeDisplayData } from 'sigma/types';

import type { SigmaNodeAttributes } from './graph-node-attributes.js';

export interface HighlightSets {
  readonly citation: ReadonlySet<string>;
  readonly tool: ReadonlySet<string>;
  readonly blast: ReadonlySet<string>;
  readonly visible: boolean;
}

export type HighlightNodeReducer = (
  node: string,
  data: SigmaNodeAttributes,
) => Partial<NodeDisplayData>;

const CITATION_COLOR = '#22c55e';
const TOOL_COLOR = '#3b82f6';
const BLAST_COLOR = '#ef4444';
const DIMMED_COLOR = '#1f2937';
export const SELECTION_COLOR = '#fbbf24';

const CITATION_SCALE = 1.4;
const TOOL_SCALE = 1.3;
const BLAST_SCALE = 1.5;
const SELECTION_SCALE = 1.6;
const NEIGHBOR_SCALE = 1.15;

const PULSE_AMPLITUDE = 0.15;

export function hasActiveHighlights(h: HighlightSets): boolean {
  return h.visible && (h.citation.size > 0 || h.tool.size > 0 || h.blast.size > 0);
}

/** Node reducer for click-selection: selected node = amber, neighbors = keep color + scale, rest = dim. */
export function makeSelectionNodeReducer(
  selectedNodeId: string,
  neighbors: ReadonlySet<string>,
): HighlightNodeReducer {
  return (node, data): Partial<NodeDisplayData> => {
    if (node === selectedNodeId) {
      return { ...data, color: SELECTION_COLOR, size: data.size * SELECTION_SCALE, zIndex: 2 };
    }
    if (neighbors.has(node)) {
      return { ...data, size: data.size * NEIGHBOR_SCALE, zIndex: 1 };
    }
    return { ...data, color: DIMMED_COLOR, zIndex: 0 };
  };
}

export function makeHighlightNodeReducer(
  highlights: HighlightSets,
  pulse: number,
): HighlightNodeReducer {
  if (!hasActiveHighlights(highlights)) {
    return (_node, data): Partial<NodeDisplayData> => data;
  }
  const pulseFactor = 1 + PULSE_AMPLITUDE * pulse;
  return (node, data): Partial<NodeDisplayData> => {
    if (highlights.citation.has(node)) {
      return { ...data, color: CITATION_COLOR, size: data.size * CITATION_SCALE * pulseFactor };
    }
    if (highlights.tool.has(node)) {
      return { ...data, color: TOOL_COLOR, size: data.size * TOOL_SCALE * pulseFactor };
    }
    if (highlights.blast.has(node)) {
      return { ...data, color: BLAST_COLOR, size: data.size * BLAST_SCALE * pulseFactor };
    }
    return { ...data, color: DIMMED_COLOR, zIndex: 0 };
  };
}

export const HIGHLIGHT_COLORS = {
  citation: CITATION_COLOR,
  tool: TOOL_COLOR,
  blast: BLAST_COLOR,
} as const;
